import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $isElementNode,
  type TextFormatType,
  type ElementFormatType,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $isHeadingNode, type HeadingTagType } from '@lexical/rich-text';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { $createCodeNode, $isCodeNode } from '@lexical/code';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';

interface InvoiceToolbarPluginProps {
  readOnly?: boolean;
}

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'code';

function getSelectedNode(selection: ReturnType<typeof $getSelection>) {
  if (!selection || !$isRangeSelection(selection)) return null;
  const anchor = selection.anchor.getNode();
  const focus = selection.focus.getNode();
  if (anchor === focus) return anchor;
  return selection.isBackward() ? focus : anchor;
}

const ToolbarButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active = false, disabled = false, title, children }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      disabled
        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
        : active
        ? 'bg-lex-accent-soft text-lex-accent'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-0.5" />;

const InvoiceToolbarPlugin: React.FC<InvoiceToolbarPluginProps> = ({ readOnly = false }) => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>('paragraph');
  const [isLink, setIsLink] = useState(false);
  const [alignment, setAlignment] = useState<string>('left');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsUnderline(selection.hasFormat('underline'));
        setIsStrikethrough(selection.hasFormat('strikethrough'));

        // Block type detection
        const anchorNode = selection.anchor.getNode();
        const topLevel = $isElementNode(anchorNode)
          ? anchorNode.getTopLevelElementOrThrow()
          : anchorNode.getParentOrThrow().getTopLevelElementOrThrow();

        if ($isHeadingNode(topLevel)) {
          setBlockType(topLevel.getTag() as BlockType);
        } else if ($isCodeNode(topLevel)) {
          setBlockType('code');
        } else {
          setBlockType('paragraph');
        }

        setAlignment(topLevel.getFormatType() || 'left');

        // Link detection
        const selectedNode = getSelectedNode(selection);
        const parent = selectedNode?.getParent();
        setIsLink(
          selectedNode ? ($isLinkNode(selectedNode) || $isLinkNode(parent)) : false
        );
      });
    });
  }, [editor]);

  useEffect(() => {
    const unregisterCanUndo = editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload: boolean) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    const unregisterCanRedo = editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload: boolean) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      unregisterCanUndo();
      unregisterCanRedo();
    };
  }, [editor]);

  const formatText = useCallback(
    (format: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format),
    [editor]
  );

  const formatElement = useCallback(
    (format: ElementFormatType) => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format),
    [editor]
  );

  const applyBlockType = useCallback(
    (type: BlockType) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        if (type === 'paragraph') {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          // Toggle: if already this heading, revert to paragraph
          if (blockType === type) {
            $setBlocksType(selection, () => $createParagraphNode());
          } else {
            $setBlocksType(selection, () => $createHeadingNode(type as HeadingTagType));
          }
        }
      });
    },
    [editor, blockType]
  );

  const toggleLink = useCallback(() => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      setLinkUrl('');
      setShowLinkInput(true);
      setTimeout(() => linkInputRef.current?.focus(), 50);
    }
  }, [editor, isLink]);

  const applyLink = useCallback(() => {
    if (linkUrl.trim()) {
      const sanitized = linkUrl.startsWith('http://') || linkUrl.startsWith('https://') || linkUrl.startsWith('mailto:')
        ? linkUrl
        : `https://${linkUrl}`;
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized);
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const toggleCodeBlock = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (blockType === 'code') {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createCodeNode());
      }
    });
  }, [editor, blockType]);

  const insertHorizontalRule = useCallback(() => {
    editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
  }, [editor]);

  const undo = useCallback(() => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  }, [editor]);

  const redo = useCallback(() => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  }, [editor]);

  if (readOnly) return null;

  return (
    <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border border-gray-200 dark:border-gray-600 border-b-0 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
      {/* History */}
      <ToolbarButton onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M9 14L4 9l5-5" />
          <path d="M20 20a8 8 0 00-8-8H4" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M15 14l5-5-5-5" />
          <path d="M4 20a8 8 0 018-8h8" />
        </svg>
      </ToolbarButton>

      <Divider />

      {/* Text Format */}
      <ToolbarButton onClick={() => formatText('bold')} active={isBold} title="Bold (Ctrl+B)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <path d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" /><path d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => formatText('italic')} active={isItalic} title="Italic (Ctrl+I)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => formatText('underline')} active={isUnderline} title="Underline (Ctrl+U)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M6 3v7a6 6 0 006 6 6 6 0 006-6V3" /><line x1="4" y1="21" x2="20" y2="21" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => formatText('strikethrough')} active={isStrikethrough} title="Strikethrough">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="4" y1="12" x2="20" y2="12" />
          <path d="M17.5 7.5c0-2-1.5-3.5-5.5-3.5S6.5 5.5 6.5 7.5c0 2 1.5 3 5.5 4.5 4 1.5 5.5 2.5 5.5 4.5s-1.5 3.5-5.5 3.5-5.5-1.5-5.5-3.5" />
        </svg>
      </ToolbarButton>

      <Divider />

      {/* Block Type */}
      <ToolbarButton onClick={() => applyBlockType('h1')} active={blockType === 'h1'} title="Heading 1">
        <span className="text-xs font-bold w-4 text-center">H1</span>
      </ToolbarButton>
      <ToolbarButton onClick={() => applyBlockType('h2')} active={blockType === 'h2'} title="Heading 2">
        <span className="text-xs font-bold w-4 text-center">H2</span>
      </ToolbarButton>
      <ToolbarButton onClick={() => applyBlockType('h3')} active={blockType === 'h3'} title="Heading 3">
        <span className="text-xs font-bold w-4 text-center">H3</span>
      </ToolbarButton>

      <Divider />

      {/* Lists */}
      <ToolbarButton onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)} title="Bullet List">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
          <circle cx="5" cy="6" r="1" fill="currentColor" /><circle cx="5" cy="12" r="1" fill="currentColor" /><circle cx="5" cy="18" r="1" fill="currentColor" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)} title="Numbered List">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="10" y1="6" x2="20" y2="6" /><line x1="10" y1="12" x2="20" y2="12" /><line x1="10" y1="18" x2="20" y2="18" />
          <text x="3" y="8" fontSize="7" fill="currentColor" fontFamily="monospace">1</text>
          <text x="3" y="14" fontSize="7" fill="currentColor" fontFamily="monospace">2</text>
          <text x="3" y="20" fontSize="7" fill="currentColor" fontFamily="monospace">3</text>
        </svg>
      </ToolbarButton>

      <Divider />

      {/* Link */}
      <ToolbarButton onClick={toggleLink} active={isLink} title={isLink ? 'Remove Link' : 'Insert Link'}>
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
      </ToolbarButton>

      <Divider />

      {/* Code */}
      <ToolbarButton onClick={() => formatText('code')} title="Inline Code">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={toggleCodeBlock} active={blockType === 'code'} title="Code Block">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <polyline points="10 8 7 12 10 16" /><polyline points="14 8 17 12 14 16" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={insertHorizontalRule} title="Horizontal Rule (---)">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
      </ToolbarButton>

      <Divider />

      {/* Alignment */}
      <ToolbarButton onClick={() => formatElement('left')} active={alignment === 'left' || alignment === ''} title="Align Left">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => formatElement('center')} active={alignment === 'center'} title="Align Center">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </ToolbarButton>
      <ToolbarButton onClick={() => formatElement('right')} active={alignment === 'right'} title="Align Right">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="6" y1="18" x2="21" y2="18" />
        </svg>
      </ToolbarButton>

      {/* Inline Link URL Input */}
      {showLinkInput && (
        <>
          <Divider />
          <div className="flex items-center gap-1 ml-1">
            <input
              ref={linkInputRef}
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                if (e.key === 'Escape') { setShowLinkInput(false); setLinkUrl(''); }
              }}
              placeholder="https://..."
              className="px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-48 focus:ring-1 focus:ring-lex-accent"
            />
            <button
              type="button"
              onClick={applyLink}
              className="px-2 py-0.5 text-xs font-medium bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}
              className="px-1.5 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceToolbarPlugin;
